import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  Users, 
  CheckCircle,
  XCircle,
  BarChart3
} from 'lucide-react';
import { Link } from 'react-router-dom';
import useDashboardWebSocket from '../../hooks/useDashboardWebSocket';
import StaffAccessControl from '../auth/StaffAccessControl';

const DASHBOARD_CACHE_KEY = 'staffDashboardCache_v1';

const DEFAULT_STATS = {
  totalClients: 0,
  totalInvoices: 0,
  pendingApproval: 0,
  approved: 0,
  rejected: 0,
  paid: 0,
  overdue: 0,
  totalRevenue: 0,
  activeChats: 0,
  totalUsers: 0,
  totalUsersChange: 0,
  freeActiveSubscriptions: 0,
  freeActiveSubscriptionsChange: 0,
  paidUsers: 0,
  paidUsersChange: 0,
  cancelledSubscriptions: 0,
  cancelledSubscriptionsChange: 0,
  newSubscriptions: 0,
  newSubscriptionsChange: 0
};

const DEFAULT_SUBSCRIPTION_STATS = {
  totalActiveSubscriptions: 0,
  totalActiveSubscriptionsChange: 0,
  paidSubscriptions: 0,
  trialSubscriptions: 0,
  cancelledSubscriptions: 0,
  newSubscriptionsThisMonth: 0,
  currentMonthRevenue: 0,
  revenueGrowthRate: 0
};

const readDashboardCache = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(DASHBOARD_CACHE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch (error) {
    console.warn('Failed to read staff dashboard cache:', error);
    return null;
  }
};

const writeDashboardCache = (data) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(
      DASHBOARD_CACHE_KEY,
      JSON.stringify({ ...data, timestamp: Date.now() })
    );
  } catch (error) {
    console.warn('Failed to write staff dashboard cache:', error);
  }
};

const StaffDashboard = () => {
  const { user } = useAuth();
  const cacheRef = useRef(null);
  const hasFetchedRef = useRef(false);

  const getCachedSnapshot = () => {
    if (cacheRef.current !== null) {
      return cacheRef.current;
    }
    const snapshot = readDashboardCache();
    cacheRef.current = snapshot;
    return snapshot;
  };

  const cachedSnapshot = getCachedSnapshot();
  
  // WebSocket integration for real-time updates
  const {
    dashboardData: wsDashboardData,
    isConnected: wsConnected,
    isSubscribed: wsSubscribed,
    error: wsError
  } = useDashboardWebSocket();
  
  const [stats, setStats] = useState(cachedSnapshot?.stats || DEFAULT_STATS);
  const [recentInvoices, setRecentInvoices] = useState(cachedSnapshot?.recentInvoices || []);
  const [recentClients, setRecentClients] = useState(cachedSnapshot?.recentClients || []);
  const [monthlyRevenue, setMonthlyRevenue] = useState(cachedSnapshot?.monthlyRevenue || []);
  const [activityLogs, setActivityLogs] = useState(cachedSnapshot?.activityLogs || []);
  const [systemNotifications, setSystemNotifications] = useState(cachedSnapshot?.systemNotifications || []);
  const [subscriptionStats, setSubscriptionStats] = useState(
    cachedSnapshot?.subscriptionStats || DEFAULT_SUBSCRIPTION_STATS
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // New state variables to replace global window pollution
  const [bestMonthData, setBestMonthData] = useState(null);
  const [totalRevenue12Months, setTotalRevenue12Months] = useState(0);
  const [monthOverMonthGrowth, setMonthOverMonthGrowth] = useState(0);
  const [currentMonthRevenue, setCurrentMonthRevenue] = useState(0);

  // State for rate limiting
  const [isFetching, setIsFetching] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await fetchDashboardData();
      }
    };
    
    loadData();
    
    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, []);

  // Handle WebSocket dashboard updates
  useEffect(() => {
    if (wsDashboardData) {
      console.log('📊 Staff dashboard received WebSocket update:', wsDashboardData);
      
      // Refresh dashboard data when we receive updates
      fetchDashboardData();
    }
  }, [wsDashboardData]);

useEffect(() => {
  if (!hasFetchedRef.current) {
    return;
  }
  writeDashboardCache({
    stats,
    subscriptionStats,
    recentInvoices,
    recentClients,
    monthlyRevenue,
    activityLogs,
    systemNotifications
  });
}, [stats, subscriptionStats, recentInvoices, recentClients, monthlyRevenue, activityLogs, systemNotifications]);

  const fetchDashboardData = async (isRetry = false) => {
    // Rate limiting: prevent multiple simultaneous calls
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTime;
    const minInterval = 2000; // 2 seconds minimum between calls
    
    if (isFetching || (timeSinceLastFetch < minInterval && !isRetry)) {
      console.log('Rate limited: skipping API call');
      return;
    }
    
    try {
      setIsFetching(true);
      setLoading(true);
      setError(null);
      setLastFetchTime(now);
      
      if (isRetry) {
        setRetryCount(prev => prev + 1);
      }
      
      // Fetch CRITICAL data in parallel (FAST - DB queries only)
      const [statsResponse, invoicesResponse, clientsResponse] = await Promise.all([
        api.get('/api/dashboard/staff/stats'),
        api.get('/api/dashboard/staff/invoices/recent?limit=5'),
        api.get('/api/dashboard/staff/clients/recent?limit=5')
      ]);
      
      // Fetch OPTIONAL data in background (don't wait - they're slow Stripe calls)
      Promise.all([
        api.get('/api/dashboard/staff/subscription-stats').catch(err => {
          console.warn('Subscription stats failed (non-critical):', err.message);
          return null;
        }),
        api.get('/api/dashboard/staff/monthly-revenue').catch(err => {
          console.warn('Monthly revenue failed (non-critical):', err.message);
          return null;
        }),
        api.get('/api/dashboard/staff/activity-logs?limit=10').catch(err => {
          console.warn('Activity logs failed (non-critical):', err.message);
          return null;
        }),
        api.get('/api/dashboard/staff/system-notifications?limit=10').catch(err => {
          console.warn('System notifications failed (non-critical):', err.message);
          return null;
        })
      ]).then(([subscriptionStatsResponse, monthlyRevenueResponse, activityLogsResponse, systemNotificationsResponse]) => {
        // Process subscription stats if available
        if (subscriptionStatsResponse && subscriptionStatsResponse.data.success && subscriptionStatsResponse.data.data) {
          const subscriptionData = subscriptionStatsResponse.data.data;
          console.log('📊 Subscription stats response:', subscriptionData);
          setSubscriptionStats({
            totalActiveSubscriptions: Number(subscriptionData.totalActiveSubscriptions) || 0,
            totalActiveSubscriptionsChange: Number(subscriptionData.totalActiveSubscriptionsChange) || 0,
            paidSubscriptions: Number(subscriptionData.paidSubscriptions) || 0,
            trialSubscriptions: Number(subscriptionData.trialSubscriptions) || 0,
            cancelledSubscriptions: Number(subscriptionData.cancelledSubscriptions) || 0,
            newSubscriptionsThisMonth: Number(subscriptionData.newSubscriptionsThisMonth) || 0,
            currentMonthRevenue: Number(subscriptionData.currentMonthRevenue || subscriptionData.monthlyRecurringRevenue) || 0,
            revenueGrowthRate: Number(subscriptionData.revenueGrowthRate) || 0
          });
        } else {
          // Use data from main stats if subscription-stats failed
          const statsData = statsResponse.data.data;
          if (statsData && (statsData.totalActiveSubscriptions || statsData.monthlyRecurringRevenue)) {
            console.log('📊 Using subscription data from main stats:', statsData);
            setSubscriptionStats({
              totalActiveSubscriptions: Number(statsData.totalActiveSubscriptions) || 0,
              totalActiveSubscriptionsChange: 0,
              paidSubscriptions: Number(statsData.paidSubscriptions) || 0,
              trialSubscriptions: Number(statsData.trialSubscriptions) || 0,
              cancelledSubscriptions: 0,
              newSubscriptionsThisMonth: 0,
              currentMonthRevenue: Number(statsData.monthlyRecurringRevenue) || 0,
              revenueGrowthRate: Number(statsData.revenueGrowth) || 0
            });
          }
        }
        
        // Process monthly revenue if available
        if (monthlyRevenueResponse && monthlyRevenueResponse.data.success) {
          const revenueData = monthlyRevenueResponse.data.data;
          console.log('📊 Monthly revenue response:', revenueData);
          
          if (revenueData && typeof revenueData === 'object') {
            // Set the monthly revenue chart data
            if (Array.isArray(revenueData.monthlyRevenue)) {
              setMonthlyRevenue(revenueData.monthlyRevenue);
            } else if (Array.isArray(revenueData)) {
              setMonthlyRevenue(revenueData);
            }
            
            // Set the metric state variables
            setCurrentMonthRevenue(Number(revenueData.currentMonth) || 0);
            setTotalRevenue12Months(Number(revenueData.averageMonthly) || 0);
            
            // Find the best month from the revenue array
            let bestMonthName = 'Nov 2025';
            if (Array.isArray(revenueData.monthlyRevenue) && revenueData.monthlyRevenue.length > 0) {
              const bestMonthData = revenueData.monthlyRevenue.reduce((best, current) => 
                current.revenue > best.revenue ? current : best
              );
              bestMonthName = bestMonthData.month || 'Nov 2025';
            }
            
            setBestMonthData({ revenue: Number(revenueData.bestMonth) || 0, month: bestMonthName });
            setMonthOverMonthGrowth(Number(revenueData.growth) || 0);
            
            console.log('📊 Set metrics:', {
              currentMonth: revenueData.currentMonth,
              average: revenueData.averageMonthly,
              best: revenueData.bestMonth,
              growth: revenueData.growth
            });
          }
        }
        
        // Process activity logs if available
        if (activityLogsResponse && activityLogsResponse.data.success) {
          setActivityLogs(activityLogsResponse.data.data || []);
        }
        
        // Process system notifications if available
        if (systemNotificationsResponse && systemNotificationsResponse.data.success) {
          setSystemNotifications(systemNotificationsResponse.data.data || []);
        }
      });
      
      // Process stats immediately (don't wait for Stripe data)
      if (statsResponse.data.success && statsResponse.data.data) {
        const statsData = statsResponse.data.data;
        setStats({
          totalClients: Number(statsData.totalUsers) || 0,
          totalInvoices: Number(statsData.totalInvoices) || 0,
          pendingApproval: Number(statsData.pendingApproval) || 0,
          approved: Number(statsData.approved) || 0,
          rejected: Number(statsData.rejected) || 0,
          paid: Number(statsData.paid) || 0,
          overdue: Number(statsData.overdue) || 0,
          totalRevenue: Number(statsData.totalRevenue) || 0,
          activeChats: Number(statsData.activeChats) || 0,
          totalUsers: Number(statsData.totalUsers) || 0,
          totalUsersChange: Number(statsData.totalUsersChange) || 0,
          freeActiveSubscriptions: Number(statsData.freeActiveSubscriptions) || 0,
          freeActiveSubscriptionsChange: Number(statsData.freeActiveSubscriptionsChange) || 0,
          paidUsers: Number(statsData.paidUsers) || 0,
          paidUsersChange: Number(statsData.paidUsersChange) || 0,
          cancelledSubscriptions: Number(statsData.cancelledSubscriptions) || 0,
          cancelledSubscriptionsChange: Number(statsData.cancelledSubscriptionsChange) || 0,
          newSubscriptions: Number(statsData.newSubscriptions) || 0,
          newSubscriptionsChange: Number(statsData.newSubscriptionsChange) || 0
        });
      }
      
      // Process recent invoices immediately
      if (invoicesResponse.data.success && Array.isArray(invoicesResponse.data.data)) {
        setRecentInvoices(invoicesResponse.data.data);
      }
      
      // Process recent clients immediately
      if (clientsResponse.data.success && Array.isArray(clientsResponse.data.data)) {
        setRecentClients(clientsResponse.data.data);
      }

      hasFetchedRef.current = true;
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      
      // Provide more specific error messages
      if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          setError('Authentication required. Please log in again.');
        } else if (error.response.status === 403) {
          setError('Access denied. You do not have permission to view this dashboard.');
        } else if (error.response.status === 500) {
          setError('Server error. Please try again later or contact support.');
        } else {
          setError(`Failed to fetch dashboard data (${error.response.status}). Please try again.`);
        }
      } else if (error.request) {
        // Network error
        setError('Network error. Please check your connection and try again.');
      } else {
        // Other error
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
      setIsFetching(false); // Reset fetching state
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    // Safely handle undefined, null, or invalid amounts
    const safeAmount = amount && !isNaN(amount) ? Number(amount) : 0;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency || 'GBP',
      minimumFractionDigits: 2
    }).format(safeAmount);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending_approval: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      paid: 'bg-blue-100 text-blue-800',
      overdue: 'bg-red-100 text-red-800',
      draft: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.draft;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending_approval: '⏳',
      pending: '⏳',
      approved: '✅',
      rejected: '❌',
      paid: '💰',
      overdue: '⚠️',
      draft: '📝'
    };
    return icons[status] || icons.draft;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending_approval: 'Pending Approval',
      pending: 'Pending Approval',
      approved: 'Approved',
      rejected: 'Rejected',
      paid: 'Paid',
      overdue: 'Overdue',
      draft: 'Draft'
    };
    return labels[status] || labels.draft;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg border shadow-sm p-6">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <XCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
                <button
                  onClick={() => fetchDashboardData(true)}
                  className="mt-3 bg-red-100 text-red-800 px-3 py-1 rounded text-sm hover:bg-red-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={retryCount >= 3 || loading}
                  aria-label={`Retry loading dashboard data (attempt ${retryCount + 1} of 3)`}
                >
                  {loading ? 'Loading...' : retryCount >= 3 ? 'Max retries reached' : `Try Again (${retryCount + 1}/3)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <StaffAccessControl>
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Staff Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">System-wide overview and management</p>
        </div>

        {/* Revenue Trend Chart - 12 Months with Scroll */}
        <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2 sm:mb-0">Revenue Trend - Past 12 Months</h3>
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0">
              <div className="text-xs sm:text-sm text-gray-500">
                Total Revenue: <span className="font-bold text-green-600">{formatCurrency(totalRevenue12Months || 0)}</span>
              </div>
              <div className="text-xs sm:text-sm text-gray-500">
                Growth: <span className={`font-bold ${monthOverMonthGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {monthOverMonthGrowth >= 0 ? '+' : ''}{monthOverMonthGrowth}%
                </span>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              <div className="grid grid-cols-12 gap-2 mb-4">
                                  {monthlyRevenue.length > 0 ? (
                    monthlyRevenue.map((month, i) => {
                    
                    return (
                      <div key={i} className="text-center">
                        <div className="text-xs text-gray-500 mb-1">{month.month}</div>
                        <div className="bg-gray-100 rounded-lg p-2 h-24 flex flex-col justify-end">
                          <div className="text-xs font-medium text-gray-700 mb-1">
                            {formatCurrency(month.revenue)}
                          </div>
                          <div 
                            className={`rounded-t ${month.revenue >= currentMonthRevenue ? 'bg-green-500' : 'bg-red-500'}`}
                            style={{ 
                              height: `${Math.max(10, (month.revenue / (bestMonthData?.revenue || 1)) * 60)}px`,
                              minHeight: '10px'
                            }}
                          ></div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  // Loading state for revenue chart
                  Array.from({ length: 12 }, (_, i) => (
                    <div key={i} className="text-center">
                      <div className="text-xs text-gray-500 mb-1">--</div>
                      <div className="bg-gray-100 rounded-lg p-2 h-24 flex flex-col justify-end">
                        <div className="text-xs font-medium text-gray-700 mb-1">
                          {formatCurrency(0)}
                        </div>
                        <div className="rounded-t bg-gray-300" style={{ height: '10px' }}></div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <div className="text-center text-sm text-gray-500">
                Scroll horizontally to view older months • Hover over bars for details
              </div>
            </div>
          </div>
          
          <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 text-center">
            <div className="bg-green-50 p-3 sm:p-4 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-600">Current Month</p>
              <p className="text-lg sm:text-2xl font-bold text-green-600">{formatCurrency(currentMonthRevenue || 0)}</p>
            </div>
            <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-600">Average Monthly</p>
              <p className="text-lg sm:text-2xl font-bold text-blue-600">{formatCurrency(Math.round(totalRevenue12Months / 12))}</p>
            </div>
            <div className="bg-purple-50 p-3 sm:p-4 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-600">Best Month</p>
              <p className="text-lg sm:text-2xl font-bold text-purple-600">{formatCurrency(bestMonthData?.revenue || 0)}</p>
              <p className="text-xs text-gray-500">{bestMonthData?.month || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Charts Section - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Subscription Business Overview */}
          <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Subscription Business Overview</h3>
            <div className="h-64 flex items-center justify-center">
              {loading ? (
                <div className="text-gray-500">Loading...</div>
              ) : (
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{subscriptionStats.totalActiveSubscriptions}</div>
                  <div className="text-sm text-gray-500">Total Active Subscriptions</div>
                  <div className="text-2xl font-bold text-blue-600 mt-4">{formatCurrency(subscriptionStats.currentMonthRevenue)}</div>
                  <div className="text-sm text-gray-500">Monthly Revenue</div>
                </div>
              )}
            </div>
            <div className="mt-12 grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-gray-500">Paid Subscriptions</p>
                <p className="text-2xl font-bold text-green-600">{subscriptionStats.paidSubscriptions}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Trial Subscriptions</p>
                <p className="text-2xl font-bold text-blue-600">{subscriptionStats.trialSubscriptions}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Growth Rate</p>
                <p className={`text-2xl font-bold ${subscriptionStats.totalActiveSubscriptionsChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {subscriptionStats.totalActiveSubscriptionsChange >= 0 ? '+' : ''}{subscriptionStats.totalActiveSubscriptionsChange}%
                </p>
              </div>
            </div>
          </div>

          {/* Subscription Growth Overview */}
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Subscription Growth Overview</h3>
            <div className="h-64 flex items-center justify-center">
              {loading ? (
                <div className="text-gray-500">Loading...</div>
              ) : (
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{subscriptionStats.trialSubscriptions}</div>
                  <div className="text-sm text-gray-500">Trial Subscriptions</div>
                  <div className="text-2xl font-bold text-green-600 mt-4">{subscriptionStats.newSubscriptionsThisMonth}</div>
                  <div className="text-sm text-gray-500">New This Month</div>
                </div>
              )}
            </div>
            <div className="mt-6 grid grid-cols-4 gap-4 text-center">
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Paid Subscriptions</p>
                <p className="text-2xl font-bold text-purple-600">{subscriptionStats.paidSubscriptions}</p>
                <p className={`text-xs ${subscriptionStats.revenueGrowthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {subscriptionStats.revenueGrowthRate >= 0 ? '+' : ''}{subscriptionStats.revenueGrowthRate}% revenue growth
                </p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Trial Subscriptions</p>
                <p className="text-2xl font-bold text-green-600">{subscriptionStats.trialSubscriptions}</p>
                <p className={`text-xs ${subscriptionStats.totalActiveSubscriptionsChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {subscriptionStats.totalActiveSubscriptionsChange >= 0 ? '+' : ''}{subscriptionStats.totalActiveSubscriptionsChange}% vs last month
                </p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">New This Month</p>
                <p className="text-2xl font-bold text-blue-600">{subscriptionStats.newSubscriptionsThisMonth}</p>
                <p className="text-xs text-gray-500">
                  New subscriptions
                </p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Cancelled</p>
                <p className="text-2xl font-bold text-red-600">{subscriptionStats.cancelledSubscriptions}</p>
                <p className="text-xs text-gray-500">
                  Cancelled subscriptions
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Invoices */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Recent Invoices</h3>
                <Link
                  to="/staff/invoices"
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  View all
                </Link>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentInvoices.length > 0 ? (
                    recentInvoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {invoice.invoiceNumber}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatCurrency(invoice.total, invoice.currency)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {invoice.sender?.company || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span 
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}
                            role="status"
                            aria-label={`Invoice status: ${getStatusLabel(invoice.status)}`}
                          >
                            {getStatusIcon(invoice.status)}
                            <span className="ml-1">{getStatusLabel(invoice.status)}</span>
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                        No recent invoices
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Clients */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Recent Clients</h3>
                <Link
                  to="/staff/clients"
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  View all
                </Link>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentClients.length > 0 ? (
                    recentClients.map((client) => (
                      <tr key={client.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {client.contactName || 'Unknown User'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {client.companyName || 'Individual User'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{client.email}</div>
                        </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                client.status === 'pro' ? 'bg-green-100 text-green-800' :
                client.status === 'essential' ? 'bg-blue-100 text-blue-800' :
                client.status === 'trial' ? 'bg-yellow-100 text-yellow-800' :
                client.status === 'free' ? 'bg-gray-100 text-gray-800' :
                client.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                client.status === 'past_due' ? 'bg-orange-100 text-orange-800' :
                client.status === 'unpaid' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {client.status === 'pro' ? '✅ Pro' :
                 client.status === 'essential' ? '🔧 Essential' :
                 client.status === 'trial' ? '🆓 Trial' :
                 client.status === 'free' ? '🆓 Free' :
                 client.status === 'cancelled' ? '❌ Cancelled' :
                 client.status === 'past_due' ? '⚠️ Past Due' :
                 client.status === 'unpaid' ? '💳 Unpaid' :
                 '❓ Unknown'}
              </span>
            </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                        No recent clients
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Recent Activities and System Notifications */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 mt-8">
          {/* Recent Activities */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Recent Activities</h3>
                <Link
                  to="/staff/activities"
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  View all
                </Link>
              </div>
            </div>
            
            <div className="p-6">
              {activityLogs.length > 0 ? (
                <div className="space-y-4">
                  {activityLogs.slice(0, 5).map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">{activity.staffName}</span> - {activity.details?.message || activity.action}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(activity.timestamp).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <p>No recent activities</p>
                </div>
              )}
            </div>
          </div>

          {/* System Notifications */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">System Notifications</h3>
                <Link
                  to="/staff/notifications"
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  View all
                </Link>
              </div>
            </div>
            
            <div className="p-6">
              {systemNotifications.length > 0 ? (
                <div className="space-y-4">
                  {systemNotifications.slice(0, 5).map((notification) => (
                    <div key={notification.id} className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          notification.priority === 'high' ? 'bg-red-500' :
                          notification.priority === 'medium' ? 'bg-yellow-500' :
                          'bg-green-500'
                        }`}></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        {notification.metadata?.details && (
                          <div className="text-xs text-gray-500 mt-2">
                            {notification.metadata.details.map((detail, index) => (
                              <div key={index} className="mt-1">
                                {detail.eventType && (
                                  <span>Event: {detail.eventType}</span>
                                )}
                                {detail.userEmail && (
                                  <span className="ml-2">User: {detail.userEmail}</span>
                                )}
                                {detail.companyName && (
                                  <span className="ml-2">Company: {detail.companyName}</span>
                                )}
                                {detail.expiresAt && (
                                  <span className="ml-2">Expires: {new Date(detail.expiresAt).toLocaleDateString('en-GB')}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {notification.metadata && (
                          <div className="text-xs text-gray-500 mt-2">
                            {notification.metadata.cpuPercentage && (
                              <span>CPU: {notification.metadata.cpuPercentage}%</span>
                            )}
                            {notification.metadata.memoryPercentage && (
                              <span className="ml-2">Memory: {notification.metadata.memoryPercentage}%</span>
                            )}
                            {notification.metadata.responseTime && (
                              <span className="ml-2">DB: {notification.metadata.responseTime}ms</span>
                            )}
                            {notification.metadata.usagePercentage && (
                              <span className="ml-2">Disk: {notification.metadata.usagePercentage}%</span>
                            )}
                            {notification.metadata.errorPercentage && (
                              <span className="ml-2">Errors: {notification.metadata.errorPercentage}%</span>
                            )}
                            {notification.metadata.avgResponseTime && (
                              <span className="ml-2">API: {notification.metadata.avgResponseTime}ms</span>
                            )}
                            {notification.metadata.connectedClients && (
                              <span className="ml-2">WS: {notification.metadata.connectedClients} clients</span>
                            )}
                          </div>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(notification.created_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <p>No system notifications</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </StaffAccessControl>
  );
};

export default StaffDashboard;
